import { Node } from "@xyflow/react";
import { createEdge, Edge } from "~/lib/pipeline/graph/Edge";
import { NodeType } from "~/lib/pipeline/graph/NodeType";
import { EdgeType } from "~/lib/pipeline/graph/EdgeType";
import { AdjacencyMap } from "~/lib/pipeline/graph/AdjacencyMap";

/**
 * Remap edges that connect nodes between different hierarchy levels.
 *
 * This function handles collapsed groups by remapping edges from group children
 * to their parent group nodes, while ensuring no cycles are created in the process.
 */
export function remapCollapsedGroupEdges(
  adjacencyMap: AdjacencyMap,
  nodes: Map<string, Node>,
  edges: Map<string, Edge>,
): void {
  const collapsed = new Set(
    Array.from(nodes.values())
      .filter((node) => node.type === NodeType.Group && !node.expandParent)
      .map((node) => node.id),
  );

  if (collapsed.size === 0) {
    return;
  }

  // Process each edge that crosses group boundaries
  const edgesToProcess = Array.from(edges.values()).filter(
    (edge) => edge.data?.sourceParentId !== edge.data?.targetParentId,
  );

  for (const edge of edgesToProcess) {
    // Find the effective source (parent if child is in collapsed group)
    let source = nodes.get(edge.source);
    if (source?.parentId && collapsed.has(source.parentId)) {
      source = nodes.get(source.parentId);
    }

    // Find the effective target (parent if child is in collapsed group)
    let target = nodes.get(edge.target);
    if (target?.parentId && collapsed.has(target.parentId)) {
      target = nodes.get(target.parentId);
    }

    // Remove the original edge
    edges.delete(edge.id);
    adjacencyMap.delete(edge.source, edge.target);

    // Create new edge if needed
    if (source && target && source.id !== target.id) {
      if (!adjacencyMap.reachable(target.id, source.id)) {
        const newEdge = createEdge(source, target, edge.type as EdgeType);
        edges.set(newEdge.id, newEdge);
        adjacencyMap.add(newEdge.source, newEdge.target);
      }
    }
  }

  // Remove child nodes of collapsed groups
  Array.from(nodes.values())
    .filter((node) => node.parentId && collapsed.has(node.parentId))
    .forEach((node) => nodes.delete(node.id));

  // Remove internal edges within collapsed groups
  Array.from(edges.values())
    .filter(
      (edge) =>
        edge.data?.sourceParentId === edge.data?.targetParentId &&
        edge.data?.sourceParentId &&
        collapsed.has(edge.data?.sourceParentId),
    )
    .forEach((edge) => {
      edges.delete(edge.id);
      adjacencyMap.delete(edge.source, edge.target);
    });
}
